import { View } from 'react-native'
import { Text, Divider } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../@ui'
import { createStyle } from '../../../contexts/global-styles.provider'

type DifferentRow = {
  labelKey: string
  type: 'different'
  publicValueKey: string
  privateValueKey: string
}
type TextRow = {
  labelKey: string
  type: 'text'
  sharedValueKey: string
}
type CheckRow = {
  labelKey: string
  type: 'check'
}
type ComparisonRowData = CheckRow | TextRow | DifferentRow

const COMPARISON_ROWS: ComparisonRowData[] = [
  {
    labelKey: 'comparison-visibility',
    type: 'different',
    publicValueKey: 'comparison-open',
    privateValueKey: 'comparison-private-only',
  },
  {
    labelKey: 'comparison-perspectives',
    type: 'text',
    sharedValueKey: 'comparison-unlimited',
  },
  {
    labelKey: 'comparison-billing',
    type: 'text',
    sharedValueKey: 'comparison-by-active-members',
  },
  {
    labelKey: 'comparison-clusters',
    type: 'check',
  },
  {
    labelKey: 'comparison-max-cap',
    type: 'check',
  },
  {
    labelKey: 'comparison-eagle-replay',
    type: 'check',
  },
]

export const ComparisonTable = () => {
  const { t } = useTranslation('pricing')
  const styles = useStyle()

  const renderCell = (row: ComparisonRowData, column: 'public' | 'private') => {
    if (row.type === 'check') {
      return <Icon source="check-circle" size={20} color="#4CAF50" />
    }
    if (row.type === 'text') {
      return (
        <Text variant="bodySmall" style={styles.cellText}>
          {t(row.sharedValueKey)}
        </Text>
      )
    }
    const valueKey =
      column === 'public' ? row.publicValueKey : row.privateValueKey
    return (
      <Text variant="bodySmall" style={[styles.cellText, styles.boldText]}>
        {t(valueKey)}
      </Text>
    )
  }

  return (
    <View style={styles.container}>
      <Text variant="titleLarge" style={styles.title}>
        {t('comparison-title')}
      </Text>
      <View style={styles.table}>
        <View style={styles.headerRow}>
          <View style={styles.labelCell} />
          <View style={[styles.valueCell, styles.publicCell]}>
            <Text variant="labelLarge" style={styles.headerText}>
              {t('public-node-title')}
            </Text>
          </View>
          <View style={styles.valueCell}>
            <Text variant="labelLarge" style={styles.headerText}>
              {t('private-node-title')}
            </Text>
          </View>
        </View>

        {COMPARISON_ROWS.map((row, index) => (
          <View key={row.labelKey}>
            <View style={styles.row}>
              <View style={styles.labelCell}>
                <Text variant="bodySmall" style={styles.label}>
                  {t(row.labelKey)}
                </Text>
              </View>
              <View style={[styles.valueCell, styles.publicCell]}>
                {renderCell(row, 'public')}
              </View>
              <View style={styles.valueCell}>{renderCell(row, 'private')}</View>
            </View>
            {index < COMPARISON_ROWS.length - 1 && <Divider />}
          </View>
        ))}
      </View>
    </View>
  )
}

const useStyle = createStyle(({ theme }) => ({
  container: {
    marginTop: theme.spacing(10),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  table: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  headerRow: {
    flexDirection: 'row' as const,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    minHeight: 48,
    alignItems: 'stretch' as const,
  },
  row: {
    flexDirection: 'row' as const,
    minHeight: 56,
    alignItems: 'stretch' as const,
  },
  labelCell: {
    flex: 2,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(2),
    justifyContent: 'center' as const,
  },
  valueCell: {
    flex: 3,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(2),
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  publicCell: {
    backgroundColor: '#FFF5F5',
  },
  label: {
    color: '#121212cc',
  },
  headerText: {
    fontWeight: 'bold' as const,
  },
  cellText: {
    textAlign: 'center' as const,
  },
  boldText: {
    fontWeight: 'bold' as const,
  },
}))
